import { Game, Scene, Store, TextureFactory } from '../core';
import { sound } from '@pixi/sound';
import { message } from 'antd';
import { OpeningScene } from '.';
import { AnimatedSprite, Container, Graphics, InteractionEvent, Sprite, Text, Texture } from 'pixi.js';
import { StarCutAway } from '../cutaway';
import { Canvas } from '../class/main';
import { DrawingPatternData, Point, Rank } from '../interface';
import { MotionBlurFilter } from 'pixi-filters';
import { CostumeItem } from '../class';
import PeerStore from '../class/PeerStore';
import { getRanks } from '../api/rank';
import { linear, qrCodeGenerate } from '../util';
import TextInput from '../ui/TextInput';
import { LOCALE_DATA } from '../constant';
import { logging, LOG_EVENT, LOG_SCENE } from '../api/log';
import MultiplayRoom from './MultiplayRoom';

class DotIndicator extends Container {
  private range: number = 1;
  private graphics: Graphics;
  public index: number = 0;

  constructor(range: number) {
    super();

    this.range = range;
    this.graphics = new Graphics();
    this.addChild(this.graphics);
  }

  public update(index: number): void {
    this.index = index;
    this.graphics.clear();
    this.graphics.lineStyle(2, 0x4a292d);

    const baseX = -((this.range - 1) / 2) * 15;
    for (let i = 0; i < this.range; i++) {
      if (this.index === i) {
        this.graphics.beginFill(0x4a292d);
        this.graphics.drawCircle(baseX + i * 15, 0, 4);
        this.graphics.endFill();
      } else {
        this.graphics.drawCircle(baseX + i * 15, 0, 4);
      }
    }
  }
}

class RankUI extends Container {
  private scoreText: Text;
  private nameText: Text;
  private showRank: boolean = false;
  private rankDT: number = 0;
  private rankIndex: number = 0;
  private ranks: Array<Rank> = [];

  constructor() {
    super();

    const sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/trophy.png'));
    this.addChild(sprite);

    this.scoreText = new Text('', {
      fontSize: 32,
      fontWeight: '900',
      fontFamily: 'gaegu',
      fontStyle: 'normal',
      letterSpacing: -4,
      fill: '#4a282f',
      stroke: 0xffffff,
      strokeThickness: 3,
      padding: 8,
    });
    this.scoreText.y = -10;
    this.scoreText.x = 100;
    this.addChild(this.scoreText);

    this.nameText = new Text('', {
      fontSize: 45,
      fontWeight: '900',
      fontFamily: 'gaegu',
      fontStyle: 'normal',
      letterSpacing: -4,
      fill: '#4a282f',
      stroke: 0xffffff,
      strokeThickness: 3,
      padding: 8,
    });
    this.nameText.y = 15;
    this.nameText.x = 100;
    this.addChild(this.nameText);
  }

  public setRanks(ranks: Array<Rank>): void {
    this.ranks = ranks;
    this.rankIndex = 0;
    this.showRank = true;
  }

  public update(elapsed: number): void {
    if (this.showRank && this.ranks.length) {
      this.rankDT += elapsed;

      if (this.rankDT < 30) {
        this.nameText.text = `#${this.rankIndex + 1}.${this.ranks[this.rankIndex].name}`;
        this.scoreText.text = `Score ${this.ranks[this.rankIndex].score}`;

        this.nameText.y = -15 - linear((30 - this.rankDT) / 30) * 10;
        this.nameText.alpha = linear(this.rankDT / 30);

        this.scoreText.y = 35 - linear((30 - this.rankDT) / 30) * 10;
        this.scoreText.alpha = linear(this.rankDT / 30);
      } else if (this.rankDT >= 240 && this.rankDT < 270) {
        this.nameText.y = -15 + linear((this.rankDT - 240) / 30) * 10;
        this.nameText.alpha = linear((270 - this.rankDT) / 30);

        this.scoreText.y = 35 + linear((this.rankDT - 240) / 30) * 10;
        this.scoreText.alpha = linear((270 - this.rankDT) / 30);
      } else if (this.rankDT >= 270 && this.rankDT < 300) {
        this.nameText.alpha = 0;
        this.scoreText.alpha = 0;
      } else if (this.rankDT >= 300) {
        this.rankDT = 0;
        this.rankIndex++;
        this.rankIndex %= this.ranks.length;
      }
    }
  }
}

class NameUI extends Container {
  constructor() {
    super();

    const sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/user.png'));
    this.addChild(sprite);
  }
}

class CofirmButton extends Container {
  constructor() {
    super();
    const sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/btn_base.png'));
    sprite.anchor.set(0.5);
    sprite.scale.set(0.8);
    this.addChild(sprite);

    const title = new Text(LOCALE_DATA[Store.instance.state.locale].CONFIRM, {
      fontSize: 60,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      padding: 10,
    });
    title.y = -5;
    title.anchor.set(0.5);
    this.addChild(title);
  }
}

class LongButton extends Container {
  constructor(text: string) {
    super();

    const btn = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/btn_long.png'));
    btn.anchor.set(0.5);
    this.addChild(btn);

    const title = new Text(text, {
      fontSize: 50,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      padding: 10,
    });
    title.y = -5;
    title.anchor.set(0.5);
    this.addChild(title);
  }
}

class CancelButton extends Container {
  constructor() {
    super();
    const sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/btn_base.png'));
    sprite.anchor.set(0.5);
    sprite.scale.set(0.8);
    this.addChild(sprite);

    const title = new Text(LOCALE_DATA[Store.instance.state.locale].CANCEL, {
      fontSize: 60,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0xff2A2E',
      lineJoin: 'round',
      padding: 10,
    });
    title.y = -5;
    title.anchor.set(0.5);
    this.addChild(title);
  }
}

class CodeInputPopup extends Container {
  private confirmButton: CofirmButton;
  private cancelButton: CancelButton;
  public textInput: TextInput;

  constructor() {
    super();
    const sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/popup_name_change.png'));
    this.addChild(sprite);

    const title = new Text(LOCALE_DATA[Store.instance.state.locale].CODE_POPUP_TITLE, {
      fontSize: Store.instance.state.locale === 'en-US' ? 50 : 55,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0xffffff',
      stroke: '0x492A2E',
      strokeThickness: 16,
      lineJoin: 'round',
      padding: 20,
    });
    title.anchor.set(0.5);
    title.x = 400;
    title.y = 650;
    this.addChild(title);

    this.confirmButton = new CofirmButton();
    this.confirmButton.interactive = true;
    this.confirmButton.buttonMode = true;
    this.confirmButton.position.y = 980;
    this.confirmButton.position.x = 240;
    this.addChild(this.confirmButton);

    let confirmPressed: boolean = false;
    this.confirmButton.on('pointerdown', () => {
      sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
      confirmPressed = true;
      this.confirmButton.scale.set(this.confirmButton.scale.x * 0.95);
    });
    this.confirmButton.on('pointerup', () => {
      if (confirmPressed) {
        this.confirmButton.scale.set(this.confirmButton.scale.x / 0.95);
        this.emit('confirm');
      }
      confirmPressed = false;
    });
    this.confirmButton.on('pointerupoutside', () => {
      confirmPressed = false;
      this.confirmButton.scale.set(this.confirmButton.scale.x / 0.95);
    });

    this.cancelButton = new CancelButton();
    this.cancelButton.interactive = true;
    this.cancelButton.buttonMode = true;
    this.cancelButton.position.y = 980;
    this.cancelButton.position.x = 580;
    this.addChild(this.cancelButton);

    let cancelPressed: boolean = false;
    this.cancelButton.on('pointerdown', () => {
      sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
      cancelPressed = true;
      this.cancelButton.scale.set(this.cancelButton.scale.x * 0.95);
    });
    this.cancelButton.on('pointerup', () => {
      if (cancelPressed) {
        this.cancelButton.scale.set(this.cancelButton.scale.x / 0.95);
        this.emit('cancel');
      }
      cancelPressed = false;
    });
    this.cancelButton.on('pointerupoutside', () => {
      cancelPressed = false;
      this.cancelButton.scale.set(this.cancelButton.scale.x / 0.95);
    });

    this.textInput = new TextInput({
      input: {
        fontSize: '25pt',
        padding: '14px',
        width: '500px',
        color: '#26272E',
        textAlign: 'center',
      },
      box: {
        default: { fill: 0xe8e9f3, rounded: 16, stroke: { color: 0xcbcee0, width: 4 } },
        focused: { fill: 0xe1e3ee, rounded: 16, stroke: { color: 0xabafc6, width: 4 } },
        disabled: { fill: 0xdbdbdb, rounded: 16 },
      },
    });
    this.textInput.on('input', (value: string) => {
      this.textInput.text = (value.match(/([0-9]|[a-zA-Z])*/g) || []).join('').toUpperCase();
    });

    this.textInput.x = 150;
    this.textInput.y = 790;
    this.textInput.interactive = true;
    this.textInput.maxLength = 5;

    this.addChild(this.textInput);
  }
}

class NameChangePopUp extends Container {
  private confirmButton: CofirmButton;
  private cancelButton: CancelButton;
  public textInput: TextInput;

  constructor() {
    super();
    const sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/popup_name_change.png'));
    this.addChild(sprite);

    const title = new Text(LOCALE_DATA[Store.instance.state.locale].NICKNAME_POPUP_TITLE, {
      fontSize: Store.instance.state.locale === 'en-US' ? 65 : 75,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0xffffff',
      stroke: '0x492A2E',
      strokeThickness: 16,
      lineJoin: 'round',
      padding: 20,
    });
    title.anchor.set(0.5);
    title.x = 400;
    title.y = 650;
    this.addChild(title);

    this.confirmButton = new CofirmButton();
    this.confirmButton.interactive = true;
    this.confirmButton.buttonMode = true;
    this.confirmButton.position.y = 980;
    this.confirmButton.position.x = 240;
    this.addChild(this.confirmButton);

    let confirmPressed: boolean = false;
    this.confirmButton.on('pointerdown', () => {
      sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
      confirmPressed = true;
      this.confirmButton.scale.set(this.confirmButton.scale.x * 0.95);
    });
    this.confirmButton.on('pointerup', () => {
      if (confirmPressed) {
        this.confirmButton.scale.set(this.confirmButton.scale.x / 0.95);
        this.emit('confirm');
      }
      confirmPressed = false;
    });
    this.confirmButton.on('pointerupoutside', () => {
      confirmPressed = false;
      this.confirmButton.scale.set(this.confirmButton.scale.x / 0.95);
    });

    this.cancelButton = new CancelButton();
    this.cancelButton.interactive = true;
    this.cancelButton.buttonMode = true;
    this.cancelButton.position.y = 980;
    this.cancelButton.position.x = 580;
    this.addChild(this.cancelButton);

    let cancelPressed: boolean = false;
    this.cancelButton.on('pointerdown', () => {
      sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
      cancelPressed = true;
      this.cancelButton.scale.set(this.cancelButton.scale.x * 0.95);
    });
    this.cancelButton.on('pointerup', () => {
      if (cancelPressed) {
        this.cancelButton.scale.set(this.cancelButton.scale.x / 0.95);
        this.emit('cancel');
      }
      cancelPressed = false;
    });
    this.cancelButton.on('pointerupoutside', () => {
      cancelPressed = false;
      this.cancelButton.scale.set(this.cancelButton.scale.x / 0.95);
    });

    this.textInput = new TextInput({
      input: {
        fontSize: '25pt',
        padding: '14px',
        width: '500px',
        color: '#26272E',
        textAlign: 'center',
      },
      box: {
        default: { fill: 0xe8e9f3, rounded: 16, stroke: { color: 0xcbcee0, width: 4 } },
        focused: { fill: 0xe1e3ee, rounded: 16, stroke: { color: 0xabafc6, width: 4 } },
        disabled: { fill: 0xdbdbdb, rounded: 16 },
      },
    });

    this.textInput.x = 150;
    this.textInput.y = 790;
    this.textInput.interactive = true;
    this.textInput.placeholder =
      Store.instance.state.locale === 'en-US' ? "What's your name?" : '닉네임을 입력해 주세요';
    this.textInput.maxLength = 8;

    this.addChild(this.textInput);

    const notification = new Text(LOCALE_DATA[Store.instance.state.locale].NOTIFICATION_1, {
      fontSize: Store.instance.state.locale === 'en-US' ? 30 : 30,
      fontFamily: 'gaegu',
      fontWeight: '200',
      fontStyle: 'normal',
      fill: '0xffffff',
      lineJoin: 'round',
      padding: 20,
    });
    notification.anchor.set(0.5);
    notification.x = 400;
    notification.y = 1120;
    this.addChild(notification);
  }
}

class RankingBoard extends Container {
  private rankTextes: Text[] = [];
  private confirmButton: CofirmButton;

  constructor() {
    super();
    const sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/popup_ranking_board.png'));
    this.addChild(sprite);

    const title = new Text(LOCALE_DATA[Store.instance.state.locale].TOP_10_RANK_BOARD, {
      fontSize: 67,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0xffffff',
      stroke: '0x492A2E',
      strokeThickness: 16,
      lineJoin: 'round',
      padding: 20,
    });
    title.anchor.set(0.5);
    title.x = 415;
    title.y = 650;
    this.addChild(title);

    this.confirmButton = new CofirmButton();
    this.confirmButton.interactive = true;
    this.confirmButton.buttonMode = true;
    this.confirmButton.position.y = 1480;
    this.confirmButton.position.x = 410;
    this.addChild(this.confirmButton);

    let confirmPressed: boolean = false;
    this.confirmButton.on('pointerdown', () => {
      sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
      confirmPressed = true;
      this.confirmButton.scale.set(this.confirmButton.scale.x * 0.95);
    });
    this.confirmButton.on('pointerup', () => {
      if (confirmPressed) {
        this.confirmButton.scale.set(this.confirmButton.scale.x / 0.95);
        this.emit('confirm');
      }
      confirmPressed = false;
    });
    this.confirmButton.on('pointerupoutside', () => {
      confirmPressed = false;
      this.confirmButton.scale.set(this.confirmButton.scale.x / 0.95);
    });
  }

  private clearTextes(): void {
    for (const text of this.rankTextes) {
      text.parent.removeChild(text);
      text.destroy();
    }
    this.rankTextes = [];
  }

  public setRankingList(ranks: Array<Rank>): void {
    this.clearTextes();

    let length: number = ranks.length >= 10 ? 10 : ranks.length;
    for (let i = 0; i < length; i++) {
      const en = ['st', 'nd', 'rd'];
      const text = `[${i + 1}${Store.instance.state.locale === 'en-US' ? (i > 2 ? 'th' : en[i]) : '위'}] ${
        ranks[i].score
      }${Store.instance.state.locale === 'en-US' ? ' Score' : '점'} - ${ranks[i].name}`;
      const rankText = new Text(text, {
        fontSize: 40,
        fontFamily: 'gaegu',
        fontWeight: '400',
        fontStyle: 'normal',
        fill: '0xffffff',
        stroke: '0x492A2E',
        strokeThickness: 8,
        lineJoin: 'round',
        padding: 10,
      });
      rankText.x = 100 + Math.floor(i / 10) * 330;
      rankText.y = 740 + (i % 10) * 64;
      this.rankTextes.push(rankText);

      this.addChild(rankText);
    }
  }
}

class MainScene extends Scene {
  private canvas: Canvas;
  private point: Point;
  private ninimo?: Sprite;
  private nameChangePopup!: NameChangePopUp;
  private codePopup!: CodeInputPopup;
  private isBgmPlaying: boolean = false;
  private dotIndicator: DotIndicator;
  private costumeItems: Array<CostumeItem> = [];
  private copyrightText!: Text;
  private volumeButton!: AnimatedSprite;
  private rankUI!: RankUI;
  private nameUI!: NameUI;
  private ranks: Array<Rank> = [];
  private showNamePopup: boolean = false;
  private showCodePopup: boolean = false;
  private showRankingBoardPopup: boolean = false;
  private dim!: Graphics;
  private rankingBoard!: RankingBoard;

  constructor(game: Game) {
    super(game);

    this.point = { x: 0, y: 0 };

    Store.instance.state.hp = 5;
    Store.instance.state.score = 0;
    Store.instance.state.costume = 0;

    this.createBackground();
    this.createInstagramButton();
    this.createGameStartButton();

    this.camera.setBoundary({
      min: { x: -1080, y: 0 },
      max: { x: 2160, y: 1920 },
    });
    this.camera.positionChasingCoefficient = 0.08;
    this.camera.setTargetPosition({ x: 540, y: 960 }, true);

    this.dotIndicator = new DotIndicator(3);
    this.container.addChild(this.dotIndicator);

    this.canvas = new Canvas();
    this.canvas.on('drawing-pattern-found', (result: { similarity: number; patternData?: DrawingPatternData }) => {
      if (this.showNamePopup || this.showRankingBoardPopup) return;
      if (result.patternData?.pattern === 0) {
        const IS_RIGHT_DIRECTION = this.canvas.points[0].x < this.canvas.points[this.canvas.points.length - 1].x;
        if (IS_RIGHT_DIRECTION && this.camera.targetPosition.x > -540) {
          sound.play('/audio/swipe.mp3', { loop: false, volume: Store.instance.state.volume as number });
          this.camera.setTargetPosition({ x: this.camera.targetPosition.x - 1080, y: 960 });
        } else if (!IS_RIGHT_DIRECTION && this.camera.targetPosition.x < 540 + 1080) {
          sound.play('/audio/swipe.mp3', { loop: false, volume: Store.instance.state.volume as number });
          this.camera.setTargetPosition({ x: this.camera.targetPosition.x + 1080, y: 960 });
        }
      } else if (result.patternData?.pattern === 1) {
      }
    });
    this.container.addChild(this.canvas);

    if (!Store.instance.data.nameChanged) {
      this.showNamePopup = true;
      this.dim.visible = true;
      this.nameChangePopup.textInput.htmlInput.value = Store.instance.data.playername;
      Store.instance.data.nameChanged = true;
      Store.instance.save();
    }
  }

  public createBackground() {
    const bg = new Sprite(TextureFactory.instance.getSingleTexture('/textures/bg/bg_main.png'));
    this.stage.addChild(bg);

    const bgSelect = new Sprite(TextureFactory.instance.getSingleTexture('/textures/bg/bg_main_select.png'));
    bgSelect.x = -1080;
    this.stage.addChild(bgSelect);

    const bgMultiplay = new Sprite(TextureFactory.instance.getSingleTexture('/textures/bg/bg_multiplay.png'));
    bgMultiplay.x = 1080;
    this.stage.addChild(bgMultiplay);

    this.copyrightText = new Text('Copyright © The Pinkfong Company, Inc. All Rights Reserved.', {
      fontSize: 28,
      fill: 0xffffff,
      stroke: 0x000000,
      strokeThickness: 4,
      dropShadow: true,
      dropShadowDistance: 4,
      fontWeight: '700',
      fontFamily: 'ff-providence-sans-web-pro, sans-serif',
      fontStyle: 'normal',
      padding: 4,
    });

    this.copyrightText.anchor.set(0.5);
    this.copyrightText.x = this.game.width / 2;
    this.copyrightText.y = this.game.height - 20;
    this.uiContainer.addChild(this.copyrightText);

    const inventoryTitle = new Text(LOCALE_DATA[Store.instance.state.locale].NINIMO_INVENTORY, {
      fontSize: Store.instance.state.locale === 'en-US' ? 65 : 90,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0xffffff',
      stroke: '0x492A2E',
      strokeThickness: 16,
      lineJoin: 'round',
      padding: 20,
    });
    inventoryTitle.anchor.set(0.5);
    inventoryTitle.x = -540;
    inventoryTitle.y = 650;
    this.stage.addChild(inventoryTitle);

    const balloonChatText = new Text(LOCALE_DATA[Store.instance.state.locale].CHAT_GET_ITEM, {
      fontSize: Store.instance.state.locale === 'en-US' ? 35 : 45,
      fontFamily: 'gaegu',
      fontWeight: '900',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      padding: 10,
      letterSpacing: -4,
    });
    balloonChatText.x = -555;
    balloonChatText.y = 315;
    balloonChatText.anchor.set(0.5);
    this.stage.addChild(balloonChatText);

    const hintTexture = TextureFactory.instance.getSingleTexture('/textures/ui/hint.png');
    const hintSprite = new Sprite(hintTexture);
    hintSprite.x = -540;
    hintSprite.y = 1275;
    hintSprite.anchor.set(0.5);
    this.stage.addChild(hintSprite);

    const hintText = new Text(LOCALE_DATA[Store.instance.state.locale].ITEM_DESCRIPTION_1, {
      fontSize: Store.instance.state.locale === 'en-US' ? 34 : 40,
      fontFamily: 'gaegu',
      fontWeight: '900',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      padding: 10,
      letterSpacing: -4,
    });
    hintText.x = -540;
    hintText.y = 1280;
    hintText.anchor.set(0.5);
    this.stage.addChild(hintText);

    const hints = [
      {
        text: LOCALE_DATA[Store.instance.state.locale].ITEM_DESCRIPTION_1,
        fontSize: Store.instance.state.locale === 'en-US' ? 34 : 40,
      },
      {
        text: LOCALE_DATA[Store.instance.state.locale].ITEM_DESCRIPTION_2,
        fontSize: Store.instance.state.locale === 'en-US' ? 28 : 40,
      },
      {
        text: LOCALE_DATA[Store.instance.state.locale].ITEM_DESCRIPTION_3,
        fontSize: Store.instance.state.locale === 'en-US' ? 35 : 35,
      },
      {
        text: LOCALE_DATA[Store.instance.state.locale].ITEM_DESCRIPTION_4,
        fontSize: Store.instance.state.locale === 'en-US' ? 38 : 45,
      },
      {
        text: LOCALE_DATA[Store.instance.state.locale].ITEM_DESCRIPTION_5,
        fontSize: Store.instance.state.locale === 'en-US' ? 28 : 42,
      },
      {
        text: LOCALE_DATA[Store.instance.state.locale].ITEM_DESCRIPTION_6,
        fontSize: Store.instance.state.locale === 'en-US' ? 28 : 45,
      },
    ];

    const texture = TextureFactory.instance.getSingleTexture('/textures/icons/instagram.png');
    const button = new Sprite(texture);
    button.scale.set(0.5);
    button.visible = false;
    button.interactive = true;
    button.buttonMode = true;
    button.position.set(-365, 1257);
    button.on('pointerdown', () => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
    });
    button.on('pointerup', () => {
      window.location.href = 'instagram://user?username=ninimo.official';

      // Costume 없을경우 강제로 Instagram 이동하면서 Costume 발급한다.
      if (!Store.instance.data.costumes[5]) {
        setTimeout(() => {
          Store.instance.data.costumes[5] = true;
          Store.instance.save();
          window.location.href = 'https://instagram.com/ninimo.official';
        }, 2000);
      }
      logging(
        LOG_EVENT.REDIRECT_INSTAGRAM,
        LOG_SCENE.MAIN_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });
    this.stage.addChild(button);

    for (let i = 1; i <= 6; i++) {
      const costumeButton = new CostumeItem(i);

      costumeButton.x = -750 + ((i - 1) % 3) * 210;
      costumeButton.y = 860 + Math.floor((i - 1) / 3) * 210;
      costumeButton.buttonMode = true;
      costumeButton.interactive = true;
      costumeButton.selected = i === 1;
      costumeButton.on('pointerdown', () => {
        sound.play('/audio/item_touch.mp3', { loop: false, volume: Store.instance.state.volume as number });
        for (const costumeItem of this.costumeItems) {
          costumeItem.selected = false;
        }
        if (i === 5) {
          hintText.x = -570;
          button.visible = true;
        } else {
          hintText.x = -540;
          button.visible = false;
        }
        costumeButton.selected = true;
        hintText.text = hints[i - 1].text;
        hintText.style.fontSize = hints[i - 1].fontSize;
      });

      this.stage.addChild(costumeButton);
      this.costumeItems.push(costumeButton);
    }

    // Multiplay
    const qrMask = new Graphics();
    qrMask.beginFill(0xffffff);
    qrMask.drawRoundedRect(1470, 500, 300, 300, 25);
    qrMask.endFill();
    this.stage.addChild(qrMask);
    const qrSprite = new Sprite(Texture.EMPTY);
    qrSprite.width = 300;
    qrSprite.height = 300;
    qrSprite.anchor.set(0.5);
    qrSprite.x = 1620;
    qrSprite.y = 650;
    qrSprite.mask = qrMask;
    this.stage.addChild(qrSprite);

    const codeTitleText = new Text(LOCALE_DATA[Store.instance.state.locale].INVITATION_CODE, {
      fontSize: 60,
      fontFamily: 'gaegu',
      fontWeight: '900',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      padding: 10,
      stroke: 0xffffff,
      strokeThickness: 14,
      letterSpacing: 20,
    });
    codeTitleText.x = 1620;
    codeTitleText.y = 340;
    codeTitleText.anchor.set(0.5);
    this.stage.addChild(codeTitleText);

    const codeText = new Text('', {
      fontSize: 60,
      fontFamily: 'Consolas, monaco, monospace',
      fontWeight: '900',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      padding: 10,
      stroke: 0xffffff,
      strokeThickness: 14,
      letterSpacing: 20,
    });
    codeText.x = 1620;
    codeText.y = 430;
    codeText.anchor.set(0.5);
    this.stage.addChild(codeText);

    const hostButton = new LongButton(LOCALE_DATA[Store.instance.state.locale].HOST);
    let hostButtonPressed: boolean = false;
    hostButton.x = 1620;
    hostButton.y = 920;
    hostButton.interactive = true;
    hostButton.buttonMode = true;
    hostButton.on('pointerdown', () => {
      sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
      hostButtonPressed = true;
      hostButton.scale.set(hostButton.scale.x * 0.95);
    });
    hostButton.on('pointerup', async () => {
      if (hostButtonPressed) {
        hostButton.scale.set(hostButton.scale.x / 0.95);
        hostButtonPressed = false;

        try {
          // TODO: Signal Server
          const code: string | undefined = await PeerStore.instance.peer.connectSignalSocket(
            'https://ninimo.thepinkfong.com',
            'DEFAULT',
            Store.instance.data.code
          );
          if (code) {
            logging(
              LOG_EVENT.CREATE_ROOM,
              LOG_SCENE.MAIN_SCENE,
              Store.instance.data.playername,
              window.navigator.userAgent
            );
            Store.instance.data.code = code;
            Store.instance.save();

            const data = await qrCodeGenerate(`https://ninimo.pinkfong.com/#/multiplay?code=${code}`);
            const texture = await Texture.fromURL(data);
            qrSprite.texture = texture;
            codeText.text = `${code}`;
            PeerStore.instance.peer.once('connected', () => {
              if (PeerStore.instance.peer.isHost) {
                message.success(
                  Store.instance.state.locale === 'en-US' ? 'Connection Success!' : '친구를 찾았다 니니!'
                );
                logging(
                  LOG_EVENT.JOIN_SUCCESS,
                  LOG_SCENE.MAIN_SCENE,
                  Store.instance.data.playername,
                  window.navigator.userAgent
                );

                const cutaway = new StarCutAway();
                cutaway.setSize(this.game.width, this.game.height);
                this.game.cutaway = cutaway;
                this.game.changeScene(MultiplayRoom);
              }
            });
          }
        } catch (e) {
          console.log(e);
        }
      }
    });
    hostButton.on('pointerupoutside', () => {
      hostButtonPressed = false;
      hostButton.scale.set(hostButton.scale.x / 0.95);
    });
    this.stage.addChild(hostButton);

    const joinButton = new LongButton(LOCALE_DATA[Store.instance.state.locale].JOIN);
    let joinButtonPressed: boolean = false;
    joinButton.x = 1620;
    joinButton.y = 1100;
    joinButton.interactive = true;
    joinButton.buttonMode = true;
    joinButton.on('pointerdown', () => {
      sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
      joinButtonPressed = true;
      joinButton.scale.set(joinButton.scale.x * 0.95);
    });
    joinButton.on('pointerup', () => {
      if (joinButtonPressed) {
        joinButton.scale.set(joinButton.scale.x / 0.95);
        this.showCodePopup = true;
        this.dim.visible = true;

        if (Store.instance.data.friendsCode) {
          this.codePopup.textInput.text = Store.instance.data.friendsCode;
        }
      }
      joinButtonPressed = false;
    });
    joinButton.on('pointerupoutside', () => {
      joinButtonPressed = false;
      joinButton.scale.set(joinButton.scale.x / 0.95);
    });
    this.stage.addChild(joinButton);

    const safari = new Text(LOCALE_DATA[Store.instance.state.locale].NOTIFICATION_SAFARI, {
      fontSize: 40,
      fontFamily: 'gaegu',
      fontWeight: '900',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      align: 'center',
      padding: 10,
      stroke: 0xffffff,
      strokeThickness: 6,
    });
    safari.x = 1620;
    safari.y = 1300;
    safari.anchor.set(0.5);
    this.stage.addChild(safari);
  }

  public createInstagramButton() {
    const texture = TextureFactory.instance.getSingleTexture('/textures/icons/instagram.png');
    const button = new Sprite(texture);
    button.scale.set(0.25);
    button.interactive = true;
    button.buttonMode = true;
    button.position.set(10, 10);
    button.on('pointerdown', () => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
    });
    button.on('pointerup', () => {
      window.location.href = 'instagram://user?username=ninimo.official';

      // Costume 없을경우 강제로 Instagram 이동하면서 Costume 발급한다.
      if (!Store.instance.data.costumes[5]) {
        setTimeout(() => {
          Store.instance.data.costumes[5] = true;
          Store.instance.save();
          window.location.href = 'https://instagram.com/ninimo.official';
        }, 2000);
      }
      logging(
        LOG_EVENT.REDIRECT_INSTAGRAM,
        LOG_SCENE.MAIN_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });
    this.uiContainer.addChild(button);

    const volumeTextures = [
      TextureFactory.instance.getSingleTexture('/textures/icons/volume_1.png'),
      TextureFactory.instance.getSingleTexture('/textures/icons/volume_0.png'),
    ];
    this.volumeButton = new AnimatedSprite(volumeTextures);
    this.volumeButton.scale.set(0.5);
    this.volumeButton.interactive = true;
    this.volumeButton.buttonMode = true;
    this.volumeButton.position.set(50, 12);
    this.volumeButton.on('pointerdown', () => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
    });
    this.volumeButton.on('pointerup', () => {
      if (Store.instance.state.volume === 0) {
        Store.instance.state.volume = 1;
        sound.volumeAll = 1;
      } else {
        Store.instance.state.volume = 0;
        sound.volumeAll = 0;
      }
    });
    this.uiContainer.addChild(this.volumeButton);

    this.rankUI = new RankUI();
    this.rankUI.scale.set(0.4);
    this.rankUI.position.x = this.game.width - 130;
    this.rankUI.position.y = 10;
    this.rankUI.buttonMode = true;
    this.rankUI.interactive = true;
    this.rankUI.on('pointerdown', () => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
      this.showRankingBoardPopup = true;
      this.dim.visible = true;
    });
    this.uiContainer.addChild(this.rankUI);

    this.nameUI = new NameUI();
    this.nameUI.scale.set(0.6);
    this.nameUI.position.x = this.game.width - 165;
    this.nameUI.position.y = 10;
    this.nameUI.buttonMode = true;
    this.nameUI.interactive = true;
    this.uiContainer.addChild(this.nameUI);

    this.nameUI.on('pointerdown', () => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
      this.showNamePopup = !this.showNamePopup;
      this.nameChangePopup.textInput.htmlInput.value = Store.instance.data.playername;
      this.dim.visible = true;
    });

    this.dim = new Graphics();
    this.dim.beginFill(0x000000, 0.7);
    this.dim.drawRect(0, 0, 1080, 1920);
    this.dim.endFill();
    this.dim.visible = false;
    this.uiContainer.addChild(this.dim);

    this.nameChangePopup = new NameChangePopUp();
    this.nameChangePopup.on('cancel', () => {
      this.showNamePopup = false;
      this.dim.visible = false;
      this.nameChangePopup.textInput.htmlInput.value = Store.instance.data.playername;
    });

    this.nameChangePopup.on('confirm', () => {
      this.showNamePopup = false;
      this.dim.visible = false;
      Store.instance.data.playername = this.nameChangePopup.textInput.htmlInput.value;
      Store.instance.save();
    });
    this.nameChangePopup.y = -3000;
    this.uiContainer.addChild(this.nameChangePopup);

    //
    this.codePopup = new CodeInputPopup();
    this.codePopup.on('cancel', () => {
      this.showCodePopup = false;
      this.dim.visible = false;
    });

    this.codePopup.on('confirm', async () => {
      this.showCodePopup = false;
      this.dim.visible = false;
      try {
        // TODO: Signal Server
        logging(LOG_EVENT.JOIN_ROOM, LOG_SCENE.MAIN_SCENE, Store.instance.data.playername, window.navigator.userAgent);
        if (this.codePopup.textInput.text?.length !== 5) throw new Error('invaild code');
        const code = await PeerStore.instance.peer.connectSignalSocket(
          'https://ninimo.thepinkfong.com',
          'DEFAULT',
          Store.instance.data.code
        );
        Store.instance.data.code = code;
        Store.instance.data.friendsCode = this.codePopup.textInput.text;
        Store.instance.save();
        await PeerStore.instance.peer.connectPeer(this.codePopup.textInput.text);
        message.success(Store.instance.state.locale === 'en-US' ? 'Connection Success!' : '친구를 찾았다 니니!');
        logging(
          LOG_EVENT.JOIN_SUCCESS,
          LOG_SCENE.MAIN_SCENE,
          Store.instance.data.playername,
          window.navigator.userAgent
        );

        const cutaway = new StarCutAway();
        cutaway.setSize(this.game.width, this.game.height);
        this.game.cutaway = cutaway;
        this.game.changeScene(MultiplayRoom);
      } catch (e) {
        console.log(e);
        message.error(Store.instance.state.locale === 'en-US' ? 'Connection Fail...' : '친구를 찾지 못했다 니니..');
        logging(LOG_EVENT.JOIN_FAIL, LOG_SCENE.MAIN_SCENE, Store.instance.data.playername, window.navigator.userAgent);
      }
    });
    this.codePopup.y = -3000;
    this.uiContainer.addChild(this.codePopup);

    this.rankingBoard = new RankingBoard();
    this.rankingBoard.y = -3000;
    this.rankingBoard.on('confirm', () => {
      this.showRankingBoardPopup = false;
      this.dim.visible = false;
    });
    this.uiContainer.addChild(this.rankingBoard);
  }

  public createGameStartButton() {
    const texture = TextureFactory.instance.getSingleTexture('/textures/ui/btn_start.png');
    const button = new Sprite(texture);
    let pressed: boolean = false;

    button.scale.set(0.4);
    button.anchor.set(0.5);
    button.interactive = true;
    button.buttonMode = true;
    button.position.set(540, 1520);

    button.on('pointerdown', () => {
      sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
      pressed = true;
      button.scale.set(button.scale.x * 0.95);
    });
    button.on('pointerup', () => {
      if (pressed) {
        const cutaway = new StarCutAway();
        cutaway.setSize(this.game.width, this.game.height);
        this.game.cutaway = cutaway;

        this.game.changeScene(OpeningScene);

        button.scale.set(button.scale.x / 0.95);
      }
      pressed = false;
    });
    button.on('pointerupoutside', () => {
      pressed = false;
      button.scale.set(button.scale.x / 0.95);
    });

    this.stage.addChild(button);

    // inventory
    const inventoryTexture = TextureFactory.instance.getSingleTexture('/textures/ui/inventory.png');
    const inventoryButton = new Sprite(inventoryTexture);
    let inventoryPressed: boolean = false;

    inventoryButton.scale.set(0.8);
    inventoryButton.anchor.set(0.5);
    inventoryButton.interactive = true;
    inventoryButton.buttonMode = true;
    inventoryButton.position.set(250, 1515);

    inventoryButton.on('pointerdown', () => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
      inventoryPressed = true;
      inventoryButton.scale.set(inventoryButton.scale.x * 0.95);
    });
    inventoryButton.on('pointerup', () => {
      if (inventoryPressed) {
        if (this.camera.targetPosition.x > -540) {
          this.camera.setTargetPosition({ x: this.camera.targetPosition.x - 1080, y: 960 });
        }
        inventoryButton.scale.set(inventoryButton.scale.x / 0.95);
      }
      inventoryPressed = false;
    });
    inventoryButton.on('pointerupoutside', () => {
      inventoryPressed = false;
      inventoryButton.scale.set(inventoryButton.scale.x / 0.95);
    });

    this.stage.addChild(inventoryButton);

    // multiplayButton
    const multiplayTexture = TextureFactory.instance.getSingleTexture('/textures/ui/btn_multi.png');
    const multiplayButton = new Sprite(multiplayTexture);
    let multiplayPressed: boolean = false;

    multiplayButton.scale.set(0.8);
    multiplayButton.anchor.set(0.5);
    multiplayButton.interactive = true;
    multiplayButton.buttonMode = true;
    multiplayButton.position.set(830, 1515);

    multiplayButton.on('pointerdown', () => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
      multiplayPressed = true;
      multiplayButton.scale.set(multiplayButton.scale.x * 0.95);
    });
    multiplayButton.on('pointerup', () => {
      if (multiplayPressed) {
        if (this.camera.targetPosition.x < 1080) {
          this.camera.setTargetPosition({ x: this.camera.targetPosition.x + 1080, y: 960 });
        }
        multiplayButton.scale.set(multiplayButton.scale.x / 0.95);
      }
      multiplayPressed = false;
    });
    multiplayButton.on('pointerupoutside', () => {
      multiplayPressed = false;
      multiplayButton.scale.set(multiplayButton.scale.x / 0.95);
    });

    this.stage.addChild(multiplayButton);

    // home
    const homeTexture = TextureFactory.instance.getSingleTexture('/textures/ui/home.png');
    const homeButton = new Sprite(homeTexture);
    let homePressed: boolean = false;

    homeButton.anchor.set(0.5);
    homeButton.scale.set(0.8);
    homeButton.interactive = true;
    homeButton.buttonMode = true;
    homeButton.position.set(-540, 1520);

    homeButton.on('pointerdown', () => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
      homePressed = true;
      homeButton.scale.set(homeButton.scale.x * 0.95);
    });
    homeButton.on('pointerup', () => {
      if (homePressed) {
        if (this.camera.targetPosition.x < 540) {
          this.camera.setTargetPosition({ x: this.camera.targetPosition.x + 1080, y: 960 });
        }
        homeButton.scale.set(homeButton.scale.x / 0.95);
      }
      homePressed = false;
    });
    homeButton.on('pointerupoutside', () => {
      homePressed = false;
      homeButton.scale.set(homeButton.scale.x / 0.95);
    });
    this.stage.addChild(homeButton);

    // home
    const rightHomeButton = new Sprite(homeTexture);
    let rightHomePressed: boolean = false;

    rightHomeButton.anchor.set(0.5);
    rightHomeButton.scale.set(-0.8, 0.8);
    rightHomeButton.interactive = true;
    rightHomeButton.buttonMode = true;
    rightHomeButton.position.set(1620, 1520);

    rightHomeButton.on('pointerdown', () => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
      rightHomePressed = true;
      rightHomeButton.scale.set(rightHomeButton.scale.x * 0.95, rightHomeButton.scale.y * 0.95);
    });
    rightHomeButton.on('pointerup', () => {
      if (rightHomePressed) {
        if (this.camera.targetPosition.x > 1080) {
          this.camera.setTargetPosition({ x: this.camera.targetPosition.x - 1080, y: 960 });
        }
        rightHomeButton.scale.set(rightHomeButton.scale.x / 0.95, rightHomeButton.scale.y / 0.95);
      }
      rightHomePressed = false;
    });
    rightHomeButton.on('pointerupoutside', () => {
      rightHomePressed = false;
      rightHomeButton.scale.set(rightHomeButton.scale.x / 0.95, rightHomeButton.scale.y / 0.95);
    });

    this.stage.addChild(rightHomeButton);
  }

  public update(elapsed: number): void {
    super.update(elapsed);

    this.canvas.update(elapsed);
    this.point.x = 0;
    this.point.y = 0;

    this.rankUI.position.x = this.game.width - 130;
    this.rankUI.position.y = 10;
    this.nameUI.position.x = this.game.width - 165;

    if (this.ninimo) {
      this.ninimo.x = 1080 / 2 - this.ninimo.width / 2 + this.point.x;
      this.ninimo.y = 1920 / 2 - this.ninimo.height / 2 + this.point.y;
      (this.ninimo.filters?.[0] as MotionBlurFilter).velocity.x = -this.point.x;
      (this.ninimo.filters?.[0] as MotionBlurFilter).velocity.y = -this.point.y;
    }

    this.dotIndicator.update((this.camera.targetPosition.x + 540) / 1080);
    this.dotIndicator.position.x = this.game.width / 2;
    this.dotIndicator.position.y = this.game.height - 38;

    this.nameChangePopup.scale.set(this.camera.zoom);
    this.nameChangePopup.y += ((this.showNamePopup ? -50 : -3000) - this.nameChangePopup.y) * 0.1 * elapsed;
    this.nameChangePopup.x = this.game.width / 2 - 410 * this.camera.zoom;

    this.codePopup.scale.set(this.camera.zoom);
    this.codePopup.y += ((this.showCodePopup ? -50 : -3000) - this.codePopup.y) * 0.1 * elapsed;
    this.codePopup.x = this.game.width / 2 - 410 * this.camera.zoom;

    this.rankingBoard.scale.set(this.camera.zoom);
    this.rankingBoard.y += ((this.showRankingBoardPopup ? -150 : -3000) - this.rankingBoard.y) * 0.1 * elapsed;
    this.rankingBoard.x = this.game.width / 2 - this.rankingBoard.width / 2;

    for (const costumeItem of this.costumeItems) {
      costumeItem.update();
    }

    if (this.copyrightText) {
      this.copyrightText.scale.set(this.game.width / 1080);
      this.copyrightText.x = this.game.width / 2;
      this.copyrightText.y = this.game.height - 20;
    }

    if (Store.instance.state.volume === 0) {
      this.volumeButton.gotoAndStop(1);
    } else {
      this.volumeButton.gotoAndStop(0);
    }

    this.rankUI.update(elapsed);
  }

  public destroy(): void {
    super.destroy();
  }

  protected pointerdown = (event: InteractionEvent) => {
    if (!this.isBgmPlaying) {
      sound.play('/audio/bgm_main.mp3', { loop: true, volume: Store.instance.state.volume as number });
      this.isBgmPlaying = true;
    }
    this.canvas.pointerdown(event);
  };

  protected pointermove = (event: InteractionEvent) => {
    this.canvas.pointermove(event);
  };

  protected pointerup = (event: InteractionEvent) => {
    this.canvas.pointerup(event);
  };
  protected keydown = (event: KeyboardEvent) => {};
  protected keyup = (event: KeyboardEvent) => {};

  public async onBegin(): Promise<void> {
    try {
      this.ranks = await getRanks();
      this.rankingBoard.setRankingList(this.ranks);
      this.rankUI.setRanks(this.ranks);
    } catch (e) {
      console.error(e);
    }
  }
}

export default MainScene;
